import api from './api';
import { ApiResponse, unwrapApiResponse } from '@/utils/api-response.util';

const BASE_URL = '/admin/ehi';

export type EhiLevel = 'low' | 'unstable' | 'good' | 'strong';

export type EhiDirection = 'physical' | 'energy' | 'clarity' | 'productivity' | 'focus' | 'emotional';

/**
 * 6 напрямків EHI, 0-100 кожен - НЕРОЗРИВНА складова EHI (рішення користувача 2026-09-23), рахуються з тих
 * самих 5 блоків, що й сам EHI, не окрема опційна метрика. Дзеркалить SixDirections з
 * api/src/modules/health-index/formulas/health-index.formulas.ts.
 */
export type EhiDirectionScores = Record<EhiDirection, number>;

/** Один збережений знімок EHI (рядок журналу). Дзеркалить EhiSnapshotJournalRow з api/src/modules/ehi. */
export interface EhiSnapshotRow {
  userId: number;
  userName: string | null;
  userEmail: string | null;
  snapshotDate: string;
  ehiValueRaw: number;
  ehiValueDisplay: number;
  ehiLevel: EhiLevel;
  isKey: boolean;
  /** null - лише у знімків до 2026-09-23 (до впровадження 6 напрямків). */
  directionScores: EhiDirectionScores | null;
  calculationVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface EhiSnapshotPage {
  items: EhiSnapshotRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface EhiOption {
  value: string;
  label: string;
}

/**
 * Рівень EHI для фільтра/легенди: лейбл + межі й людське пояснення (розділ 7 ТЗ). Дзеркалить EhiLevelOption
 * з api/src/modules/ehi/ehi-snapshots-admin.service.ts - єдине місце цих даних, звідси й скрізь на фронті.
 */
export interface EhiLevelOption extends EhiOption {
  min: number;
  max: number;
  explanation: string;
}

/** Довідники фільтрів журналу (локалізовані на бекенді). */
export interface EhiOptions {
  levels: EhiLevelOption[];
  /** Користувачі, які мають знімки: value - id, label - "Ім'я Прізвище (email)" або email. */
  users: EhiOption[];
}

/** Узагальнені дані EHI: 1 запис на користувача (EHI за 30 днів). Дзеркалить EhiSummaryListRow з api/src/modules/ehi. */
export interface EhiSummaryRow {
  userId: number;
  userName: string | null;
  userEmail: string | null;
  ehiValueRaw: number;
  ehiValueDisplay: number;
  ehiLevel: EhiLevel;
  /** Значення ЗАГАЛЬНОГО EHI до цього перерахунку - "було/стало" для динаміки; null - першого перерахунку ще не було. */
  previousEhiValueRaw: number | null;
  previousEhiValueDisplay: number | null;
  closedDaysCount: number;
  /** Вікно розрахунку і реальні знімки в ньому (динаміка, прогалини); null - запис ще не перераховано після 2026-09-22. */
  windowSnapshots: EhiWindowSnapshots | null;
  /** 6 напрямків за вікно 30 днів (forward-fill середнє); null - запис ще не перераховано після 2026-09-23. */
  directionScores: EhiDirectionScores | null;
  calculationVersion: number;
  createdAt: string;
  updatedAt: string;
}

/** Дзеркалить EhiWindowSnapshots з api/src/modules/ehi/entities/ehi-summary.entity.ts. */
export interface EhiWindowSnapshots {
  /** YYYY-MM-DD, включно. */
  dateFrom: string;
  dateTo: string;
  /** Лише реальні знімки за зростанням дати; дні вікна без запису - прогалини. */
  items: { date: string; value: number }[];
}

export interface EhiSummaryPage {
  items: EhiSummaryRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/** Підсумок перерахунку узагальнених даних (самарі з бека). */
export interface EhiRecalculateSummariesResult {
  durationMs: number;
  totalUsers: number;
  updated: number;
  skippedNoSnapshots: number;
  failed: number;
  failedUserIds: number[];
}

/** Один вхідний блок дня: факт/ціль/бал (0-100). Дзеркалить EhiBlockResult з api/src/modules/ehi/entities/ehi-snapshot.entity.ts. */
export interface EhiBlockResult {
  fact: number | null;
  target: number;
  score: number;
}

/**
 * Повний розклад розрахунку дня. Дзеркалить EhiCalculationBreakdown з api - той самий об'єкт, записаний РАЗОМ зі знімком.
 * `feeling`/`tasks` - ТИМЧАСОВО (рішення користувача 2026-09-23) константи з self-assessment онбордингу
 * (emotional -> feeling, productivity -> tasks), не реальні щоденні дані - `fact` = сама оцінка 1-5, `target` = 5.
 */
export interface EhiCalculationBreakdown {
  water: EhiBlockResult | null;
  nutrition: EhiBlockResult | null;
  activity: EhiBlockResult | null;
  feeling: EhiBlockResult | null;
  tasks: EhiBlockResult | null;
  score: number;
}

/** Деталі одного знімка: запис, збережений розклад розрахунку (лог на момент запису) й живий перерахунок (для порівняння). */
export interface EhiSnapshotDetails {
  snapshot: EhiSnapshotRow;
  /** null - знімки до впровадження логу (2026-09-23) і стартовий знімок онбордингу. */
  breakdown: EhiCalculationBreakdown | null;
  recomputed: { score: number; ehiValueRaw: number; ehiValueDisplay: number; ehiLevel: EhiLevel; explanation: string } | null;
  /** How each direction follows from the stored block scores and the matrix; null - no stored blocks. */
  directionsBreakdown: EhiDirectionsBreakdown | null;
  /** Nutrition corridor for explaining the nutrition score (share of the norm). */
  nutritionCorridor: { thresholds: EhiCorridorThresholds; source: 'stored' | 'current' };
  /** Self-assessment scale: score = (rating - min) * stepPercent. */
  ratingScale: { min: number; max: number; stepPercent: number };
}

/** Share of the norm: [fullRangeMin, fullRangeMax] - 100 points, linear down to 0 at zeroBelow / zeroAbove. */
export interface EhiCorridorThresholds {
  fullRangeMin: number;
  fullRangeMax: number;
  zeroBelow: number;
  zeroAbove: number;
}

export type EhiMatrixBlock = 'A' | 'F' | 'T' | 'N' | 'W';

export interface EhiDirectionMatrixRow {
  direction: EhiDirection;
  /** Matrix coefficients as configured. */
  weights: Record<EhiMatrixBlock, number>;
  /** Coefficients actually applied: T = N/A - T is null, the rest renormalised to their sum. */
  appliedWeights: Record<EhiMatrixBlock, number | null>;
  value: number;
}

/** Mirrors EhiDirectionsBreakdown in api (ehi-snapshots-admin.service.ts). */
export interface EhiDirectionsBreakdown {
  /** stored - matrix saved with the snapshot; current - older snapshot, current config matrix. */
  matrixSource: 'stored' | 'current';
  /** Block scores 0-100; T null = N/A. */
  blocks: Record<EhiMatrixBlock, number | null>;
  rows: EhiDirectionMatrixRow[];
}

/** Деталі розрахунку узагальнених даних одного користувача: запис, вікно, сирі знімки, перерахунок. */
export interface EhiSummaryDetails {
  summary: EhiSummaryRow;
  window: { dateFrom: string; dateTo: string };
  snapshots: { snapshotDate: string; ehiValueRaw: number; ehiValueDisplay: number; ehiLevel: EhiLevel }[];
  recomputed: { ehiValueRaw: number; ehiValueDisplay: number; ehiLevel: EhiLevel; daysUsed: number; explanation: string } | null;
  /** Середній бал (0-100) кожного блоку по РЕАЛЬНИХ знімках вікна (не forward-fill); null - блок відсутній
   * у всіх знімках вікна (T - N/A) або жоден знімок не має збереженого розкладу (до 2026-09-23). */
  blockAverages: { water: number | null; nutrition: number | null; activity: number | null; feeling: number | null; tasks: number | null } | null;
}

export interface EhiSummariesParams {
  page?: number;
  limit?: number;
  /** Пошук користувача за ім'ям, прізвищем або email. */
  search?: string;
  ehiLevel?: EhiLevel;
}

export interface EhiSnapshotsParams {
  page?: number;
  limit?: number;
  userId?: number;
  dateFrom?: string;
  dateTo?: string;
  ehiLevel?: EhiLevel;
  sort?: 'asc' | 'desc';
}

/** Один оброблений (user, day) запис у пакеті - для діагностичної таблиці. Дзеркалить EhiDailyRunBatchItem з api. */
export interface EhiDailyRunBatchItem {
  userId: number;
  date: string;
  result: 'collected' | 'no-goals' | 'start-snapshot' | 'before-onboarding' | 'out-of-window' | 'failed';
}

/**
 * Підсумок одного пакета щоденної джоби EHI. Дзеркалить EhiDailyRunBatchResult з api/src/modules/ehi.
 * Цю ж форму повертає й оновлення за дату (`refreshDate`).
 */
export interface EhiDailyRunBatchResult {
  /** Скільки користувачів ще чекають обробки за свій цільовий день (до цього пакета). */
  candidates: number;
  /** Скільки реально взято в цей пакет. */
  processed: number;
  collected: number;
  skipped: number;
  failed: number;
  items: EhiDailyRunBatchItem[];
}

/** Комплексне оновлення EHI одного користувача. Дзеркалить EhiUserRefreshResult з api/src/modules/ehi. */
export interface EhiUserRefreshResult {
  userId: number;
  range: { dateFrom: string; dateTo: string } | null;
  startSnapshotDate: string | null;
  processed: number;
  collected: number;
  skipped: number;
  failed: number;
  days: { date: string; result: EhiDailyRunBatchItem['result'] }[];
  ehiBefore: number | null;
  ehiAfter: number | null;
  summaryUpdated: boolean;
}

export const ehiService = {
  /** Довідники для фільтрів журналу (статуси, рівні, ключовість). */
  getOptions: async (): Promise<EhiOptions> => {
    const response = await api.get<ApiResponse<EhiOptions>>(`${BASE_URL}/options`);
    return unwrapApiResponse(response);
  },

  /** Журнал збережених знімків EHI (admin/superadmin). */
  getSnapshots: async (params: EhiSnapshotsParams = {}): Promise<EhiSnapshotPage> => {
    const response = await api.get<ApiResponse<EhiSnapshotPage>>(`${BASE_URL}/snapshots`, { params });
    return unwrapApiResponse(response);
  },

  /** Узагальнені дані EHI (1 запис на користувача), admin/superadmin. */
  getSummaries: async (params: EhiSummariesParams = {}): Promise<EhiSummaryPage> => {
    const response = await api.get<ApiResponse<EhiSummaryPage>>(`${BASE_URL}/summaries`, { params });
    return unwrapApiResponse(response);
  },

  /** Деталі одного знімка (admin/superadmin). */
  getSnapshotDetails: async (userId: number, date: string): Promise<EhiSnapshotDetails> => {
    const response = await api.get<ApiResponse<EhiSnapshotDetails>>(`${BASE_URL}/snapshots/${userId}/${date}`);
    return unwrapApiResponse(response);
  },

  /** Деталі розрахунку узагальнених даних одного користувача (admin/superadmin). */
  getSummaryDetails: async (userId: number): Promise<EhiSummaryDetails> => {
    const response = await api.get<ApiResponse<EhiSummaryDetails>>(`${BASE_URL}/summaries/${userId}`);
    return unwrapApiResponse(response);
  },

  /** Перерахувати узагальнені дані всіх активних користувачів за знімками за останні 30 днів (налагодження). Пише в БД. */
  recalculateSummaries: async (): Promise<EhiRecalculateSummariesResult> => {
    const response = await api.post<ApiResponse<EhiRecalculateSummariesResult>>(`${BASE_URL}/summaries/recalculate`);
    return unwrapApiResponse(response);
  },

  /**
   * Комплексне оновлення одного користувача (кнопка в рядку "Узагальнені"): знімки за останні 30 завершених днів,
   * потім загальний EHI. Системний перерахунок - без пушу, стартовий знімок не змінюється. Пише в БД.
   */
  refreshUser: async (userId: number): Promise<EhiUserRefreshResult> => {
    const response = await api.post<ApiResponse<EhiUserRefreshResult>>(`${BASE_URL}/summaries/${userId}/refresh`);
    return unwrapApiResponse(response);
  },

  /**
   * Оновити знімки за конкретну ЗАВЕРШЕНУ дату (кнопка "Оновити" + дата у вкладці "Знімки"): той самий збір дня, що й
   * нічна джоба, потім загальний EHI без пушу. Усі активні користувачі або один (`userId`). Пише в БД.
   */
  refreshDate: async (date: string, userId?: number): Promise<EhiDailyRunBatchResult> => {
    const response = await api.post<ApiResponse<EhiDailyRunBatchResult>>(`${BASE_URL}/snapshots/refresh-date`, { date, userId });
    return unwrapApiResponse(response);
  },

  /**
   * PM-панель (2026-10-14): Markdown-контекст AI-пояснення EHI для довільного клієнта, БЕЗ виклику
   * AI і без добового ліміту — продакт бачить, що саме потрапляє в контекст.
   */
  getPmAiContext: async (userId: number): Promise<string> => {
    const response = await api.get<ApiResponse<{ markdown: string }>>(`/pm/clients/${userId}/ehi-ai-context`);
    return unwrapApiResponse<{ markdown: string }>(response).markdown;
  },
};
